import { useState, useEffect, useRef } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import type { Project } from '@shared/types'
import { useLicense } from '../contexts/LicenseContext'
import { useApi } from '../contexts/ApiContext'
import { useGenerationContext, computeProgress, type TrackedJob } from '../contexts/GenerationContext'
import { formatLocalTimestamp, getTotalCurrentFtes, formatDashboardTitle } from '@tn/shared'
import { selectOrphanedSkeletons } from '../utils/project-list'

// ─── Scope Badge ──────────────────────────────────────────────────────────────

const FUNCTION_CONFIG: Record<string, { bg: string; emoji: string; label: string }> = {
  HR:                 { bg: '#EDE9FE', emoji: '👥', label: 'Human Resources' },
  Finance:            { bg: '#FEF3C7', emoji: '💰', label: 'Finance' },
  'Supply Chain':     { bg: '#FEE2E2', emoji: '🔗', label: 'Supply Chain' },
  Technology:         { bg: '#DBEAFE', emoji: '💻', label: 'Technology / IT' },
  Marketing:          { bg: '#FCE7F3', emoji: '📣', label: 'Marketing' },
  'Customer Service': { bg: '#CCFBF1', emoji: '🎧', label: 'Customer Service' },
  Legal:              { bg: '#FEF9C3', emoji: '⚖️', label: 'Legal' },
  Operations:         { bg: '#F3F4F6', emoji: '⚙️', label: 'Operations' },
}

const VISIBLE_MAX = 3

function ScopeBadge({ functionScope }: { functionScope?: string[] }) {
  const isEnterprise = !functionScope || functionScope.length === 0

  if (isEnterprise) {
    return (
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-base"
        style={{ background: '#F3F4F6' }}
        title="Full Enterprise"
      >
        🏢
      </div>
    )
  }

  const visible = functionScope.slice(0, VISIBLE_MAX)
  const overflow = functionScope.length - VISIBLE_MAX

  return (
    <div className="flex items-center gap-0.5">
      {visible.map((fn) => {
        const cfg = FUNCTION_CONFIG[fn]
        if (!cfg) return null
        return (
          <div
            key={fn}
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-base"
            style={{ background: cfg.bg }}
            title={cfg.label}
          >
            {cfg.emoji}
          </div>
        )
      })}
      {overflow > 0 && (
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-semibold bg-gray-100 text-gray-500"
          title={functionScope.slice(VISIBLE_MAX).map((fn) => FUNCTION_CONFIG[fn]?.label ?? fn).join('\n')}
        >
          +{overflow}
        </div>
      )}
    </div>
  )
}

interface LayoutContext {
  refreshProjects: () => Promise<void>
  projects: Project[]
  isDemoActive: boolean
  projectsLoading: boolean
  sortBy: 'name' | 'date'
  setSortBy: (s: 'name' | 'date') => void
  sortDir: 'asc' | 'desc'
  setSortDir: (d: 'asc' | 'desc') => void
  currentUserEmail: string
  isAdmin: boolean
  showOnlyMine: boolean
  searchQuery: string
  setSearchQuery: (q: string) => void
  hideDemoProjects: boolean
  setHideDemoProjects: (v: boolean) => void
}

function formatFte(project: Project) {
  const total = project.totalFteCount ?? (project.data ? getTotalCurrentFtes(project.data) : undefined)
  return total === undefined ? '—' : total.toLocaleString('en-US')
}

function ModeBadge({ mode }: { mode: Project['mode'] }) {
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
        mode === 'outside-in'
          ? 'bg-purple-100 text-purple-700'
          : 'bg-blue-100 text-blue-700'
      }`}
    >
      {mode === 'outside-in' ? 'AI Generated' : 'Data Upload'}
    </span>
  )
}

// Shown when a skeleton project exists but its job isn't in the local context
// (e.g. the user came back after a session timeout and syncWithServer hasn't resolved yet).
function OrphanedGeneratingCard({ project }: { project: Project }) {
  const color = project.branding?.primaryColor || '#A100FF'
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: color }} />
      <div className="pt-2">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ background: color }}
          >
            {(project.client || project.name || '?')[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">{project.name || project.client}</h3>
            <p className="text-xs text-gray-500 mt-0.5">Generating in background…</p>
          </div>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${project.mode === 'outside-in' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {project.mode === 'outside-in' ? 'AI Generated' : 'Data Upload'}
          </span>
        </div>
        <div className="mt-3">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full animate-pulse" style={{ width: '40%', background: color }} />
          </div>
          <p className="text-xs text-gray-400 mt-2">Verifying status — will be removed if no active job is found</p>
        </div>
      </div>
    </div>
  )
}

function GeneratingCard({ job, onCancel }: { job: TrackedJob; onCancel: (jobId: string) => void }) {
  const { pct, label } = computeProgress(job.progress, job.type, job.gccProgress, job.feedbackProgress)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const isQueued = job.status === 'pending'
  const isFailed = job.status === 'failed'
  const isFeedback = job.type === 'feedback'
  const color = job.color || '#A100FF'

  const badgeLabel = isFeedback ? 'Changes' : job.type === 'outside-in' ? 'AI Generated' : 'Data Upload'
  const badgeClass = isFeedback
    ? 'bg-amber-100 text-amber-700'
    : job.type === 'outside-in'
      ? 'bg-purple-100 text-purple-700'
      : 'bg-blue-100 text-blue-700'
  const cardTitle = isFeedback
    ? `${job.clientName} — Applying Changes`
    : formatDashboardTitle(job.clientName)
  const failedLabel = isFeedback ? 'Changes could not be applied' : 'Generation failed'
  const failedDetail = isFeedback
    ? (job.error || 'The change request could not be processed. Open the navigator to try again.')
    : (job.error || 'Generation failed. Please try again.')

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: color }} />
      <div className="pt-2">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ background: color }}
          >
            {(job.clientName || '?')[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">{cardTitle}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isFailed ? failedLabel : isQueued ? 'Queued — waiting to start…' : label}
            </p>
          </div>
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeClass}`}>
            {badgeLabel}
          </span>
        </div>

        {isFailed ? (
          <div className="mt-3 p-2 bg-red-50 rounded-lg text-xs text-red-600">
            {failedDetail}
          </div>
        ) : (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span>{isQueued ? 'Queued' : label}</span>
              <span className="font-medium" style={{ color }}>{isQueued ? '—' : `${pct}%`}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{ width: `${isQueued ? 0 : pct}%`, background: color }}
              />
            </div>
            {!isFeedback && (
              <div className="mt-2 flex justify-end">
                {confirmingCancel ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500">Stop generation?</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); onCancel(job.jobId) }}
                      className="text-red-500 font-medium underline"
                    >
                      Yes, cancel
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmingCancel(false) }}
                      className="text-gray-400 underline"
                    >
                      Keep going
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmingCancel(true) }}
                    className="text-xs text-red-400 hover:text-red-600 underline"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function Home() {
  const { refreshProjects, projects, isDemoActive, projectsLoading, sortBy, setSortBy, sortDir, setSortDir, currentUserEmail, isAdmin, showOnlyMine, searchQuery, setSearchQuery, setHideDemoProjects } = useOutletContext<LayoutContext>()

  const handleSortClick = (key: 'name' | 'date') => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }
  const [nowMs, setNowMs] = useState(() => Date.now())
  // Tick the clock so skeletons drop out of the in-progress set once they age
  // past MAX_GENERATION_AGE_MS while Home stays mounted (mirrors Layout).
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60 * 1000)
    return () => clearInterval(id)
  }, [])
  const [loadingDemo, setLoadingDemo] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [uploadingAll, setUploadingAll] = useState(false)
  const [uploadAllResult, setUploadAllResult] = useState<{ success: number; failed: number } | null>(null)
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameError, setRenameError] = useState('')
  const [renameSaving, setRenameSaving] = useState(false)
  const cardRenameInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { status: licenseStatus } = useLicense()
  const api = useApi()
  const { activeJobs, synced, removeJob, cancelJob } = useGenerationContext()
  const isExpired = licenseStatus?.isExpired && licenseStatus?.isActivated
  const inProgressJobs = activeJobs.filter((j) => j.status !== 'succeeded')
  const timestampsByProjectId = new Map(
    projects.map((project) => {
      const timestamp = project.updatedAt || project.createdAt
      return [project.id, { formatted: formatLocalTimestamp(timestamp), original: timestamp }] as const
    }),
  )

  // Skeleton projects whose jobs aren't tracked in context yet (orphaned).
  // Shown as in-progress cards so the user knows generation is still running,
  // even if syncWithServer() hasn't resolved yet after a session timeout.
  const orphanedSkeletons = selectOrphanedSkeletons(projects, activeJobs, currentUserEmail, nowMs)
  const orphanedSkeletonIds = orphanedSkeletons.map((p) => p.id).join(',')

  // Once the server sync completes we know which jobs are genuinely active.
  // Any skeleton still orphaned at that point has no running job — delete it.
  // Re-select against a fresh clock: `nowMs` ticks only once a minute, and a
  // skeleton that has aged past the window must not be deleted as orphaned.
  useEffect(() => {
    if (!synced || orphanedSkeletons.length === 0) return
    const toDelete = selectOrphanedSkeletons(projects, activeJobs, currentUserEmail, Date.now())
    if (toDelete.length === 0) return
    toDelete.forEach((p) => void api.deleteProject(p.id).catch(() => {}))
    void refreshProjects().catch(() => {})
    // Run only when sync completes or the orphan set changes — not on every projects/activeJobs update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced, orphanedSkeletonIds])

  const hasDemoProjects = projects.some((p) => p.isDemo)

  function startCardRename(e: React.MouseEvent, project: Project) {
    e.stopPropagation()
    setRenameValue(project.name || project.client)
    setRenameError('')
    setRenamingProjectId(project.id)
    setTimeout(() => cardRenameInputRef.current?.select(), 0)
  }

  async function confirmCardRename(project: Project) {
    if (renameSaving) return
    const trimmed = renameValue.trim()
    if (!trimmed) {
      setRenameError('Name cannot be empty')
      return
    }
    setRenameSaving(true)
    try {
      await api.saveProject({ ...project, name: trimmed, client: trimmed })
      await refreshProjects()
      setRenamingProjectId(null)
      setRenameError('')
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Failed to rename')
    } finally {
      setRenameSaving(false)
    }
  }

  function cancelCardRename(e?: React.KeyboardEvent | React.MouseEvent) {
    if (e) (e as React.MouseEvent).stopPropagation?.()
    setRenamingProjectId(null)
    setRenameError('')
  }

  const toggleDemoProjects = async () => {
    setLoadingDemo(true)
    try {
      if (hasDemoProjects) {
        if (api.platform === 'web') {
          setHideDemoProjects(true)
        } else {
          const demoProjects = projects.filter((p) => p.isDemo)
          for (const p of demoProjects) {
            await api.deleteProject(p.id)
          }
          await refreshProjects()
        }
      } else {
        if (api.platform === 'web') {
          setHideDemoProjects(false)
        } else {
          await api.listDemoDatasets()
          await refreshProjects()
        }
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingDemo(false)
    }
  }

  const handleUploadAllToWeb = async () => {
    setUploadingAll(true)
    setUploadAllResult(null)
    let success = 0
    let failed = 0
    for (const project of projects.filter((p) => !p.isDemo)) {
      const result = await api.uploadToWeb(project.id)
      if (result.success) { success++ } else { failed++ }
    }
    setUploadAllResult({ success, failed })
    setUploadingAll(false)
  }

  useEffect(() => {
    if (!uploadAllResult) return
    const timer = setTimeout(() => setUploadAllResult(null), 3000)
    return () => clearTimeout(timer)
  }, [uploadAllResult])

  const confirmDelete = async () => {
    if (!confirmDeleteId) return
    const id = confirmDeleteId
    setConfirmDeleteId(null)
    await api.deleteProject(id)
    await refreshProjects()
  }

  if (projects.length === 0 && projectsLoading) {
    return (
      <div className="p-8 animate-fade-in">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="h-7 w-44 bg-gray-200 rounded-lg animate-pulse mb-2" />
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
        {/* Card skeletons */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 relative overflow-hidden">
              {/* Brand stripe skeleton */}
              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gray-200 animate-pulse" />
              <div className="pt-2">
                <div className="flex items-start gap-3 mb-3">
                  {/* Avatar skeleton */}
                  <div className="w-10 h-10 rounded-xl bg-gray-200 animate-pulse flex-shrink-0" />
                  <div className="min-w-0 flex-1 pt-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-3/4" />
                    <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="h-5 w-24 bg-gray-100 rounded-full animate-pulse" />
                  <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div className="bg-gray-50 rounded-lg h-14 animate-pulse" />
                  <div className="bg-gray-50 rounded-lg h-14 animate-pulse" />
                  <div className="bg-gray-50 rounded-lg h-14 animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-400 mt-8 animate-pulse">Loading your talent navigators…</p>
      </div>
    )
  }

  if (projects.length === 0 && searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 animate-fade-in">
        <div className="text-center">
          <div className="text-5xl mb-3">🔍</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">No matching talent navigators</h1>
          <p className="text-gray-500 text-sm max-w-xs">
            No talent navigators match &ldquo;<span className="font-medium">{searchQuery}</span>&rdquo;. Try a different search term.
          </p>
        </div>
        <button
          onClick={() => setSearchQuery('')}
          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          Clear search
        </button>
      </div>
    )
  }

  if (projects.length === 0 || (projects.every((p) => !p.data && !p.subfunctionCount && !p.automationBuildCount && p.totalFteCount == null && !p.isDemo))) {
    if (inProgressJobs.length > 0 || orphanedSkeletons.length > 0) {
      return (
        <div className="p-8 animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Your Talent Navigators</h1>
              <p className="text-sm text-gray-500 mt-1">Generating…</p>
            </div>
            {!isExpired && !isDemoActive && (
              <button
                onClick={() => navigate('/new')}
                className="flex items-center gap-2 py-2.5 px-5 rounded-lg text-white font-semibold text-sm"
                style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
              >
                + New Navigator
              </button>
            )}
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">In Progress</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {inProgressJobs.map((job) => (
              <div key={job.jobId} className="relative">
                <GeneratingCard job={job} onCancel={(id) => void cancelJob(id)} />
                {job.status === 'failed' && (
                  <button
                    onClick={() => removeJob(job.jobId)}
                    className="absolute top-3 right-3 text-gray-300 hover:text-gray-500 text-lg leading-none"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {orphanedSkeletons.map((p) => (
              <OrphanedGeneratingCard key={p.id} project={p} />
            ))}
          </div>
        </div>
      )
    }
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6 animate-fade-in">
        <div className="text-center">
          <div className="text-6xl mb-4">📊</div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            No talent navigators yet
          </h1>
          <p className="text-gray-500 max-w-sm">
            Create your first workforce reinvention navigator — AI-generated or from client data.
          </p>
        </div>
        <div className="flex gap-3">
          {!isExpired && !isDemoActive && (
            <button
              onClick={() => navigate('/new')}
              className="flex items-center gap-2 py-3 px-8 rounded-xl text-white font-semibold text-sm transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              Create First Navigator
            </button>
          )}
          {isDemoActive && !hasDemoProjects && (
            <button
              onClick={toggleDemoProjects}
              disabled={loadingDemo}
              className="flex items-center gap-2 py-3 px-8 rounded-xl font-semibold text-sm transition-all hover:scale-105 border-2 border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
            >
              {loadingDemo ? 'Loading...' : 'Load Demo Talent Navigators'}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isAdmin && !showOnlyMine ? 'All Talent Navigators' : 'Your Talent Navigators'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{projects.length} project{projects.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sort controls */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <span className="px-2 text-xs text-gray-400 select-none">Sort:</span>
            <button
              onClick={() => handleSortClick('name')}
              className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all ${sortBy === 'name' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Name
              {sortBy === 'name' && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
            </button>
            <button
              onClick={() => handleSortClick('date')}
              className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all ${sortBy === 'date' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Date Updated
              {sortBy === 'date' && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
            </button>
          </div>
          {isDemoActive && (
            <button
              onClick={toggleDemoProjects}
              disabled={loadingDemo}
              className={`flex items-center gap-2 py-2.5 px-5 rounded-lg font-semibold text-sm border-2 transition-all disabled:opacity-50 ${
                hasDemoProjects
                  ? 'border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100'
                  : 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100'
              }`}
            >
              {loadingDemo ? 'Loading...' : hasDemoProjects ? 'Hide Demo Talent Navigators' : 'Load Demo Talent Navigators'}
            </button>
          )}
          {api.platform === 'desktop' && (
            <button
              onClick={handleUploadAllToWeb}
              disabled={uploadingAll || projects.filter((p) => !p.isDemo).length === 0}
              className="flex items-center gap-2 py-2.5 px-5 rounded-lg font-semibold text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {uploadingAll ? 'Publishing…' : 'Publish All'}
            </button>
          )}
          {!isExpired && !isDemoActive && (
            <button
              onClick={() => navigate('/new')}
              className="flex items-center gap-2 py-2.5 px-5 rounded-lg text-white font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              + New Navigator
            </button>
          )}
        </div>
      </div>

      {/* In-Progress generation cards */}
      {(inProgressJobs.length > 0 || orphanedSkeletons.length > 0) && (
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">In Progress</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {inProgressJobs.map((job) => (
              <div key={job.jobId} className="relative">
                <GeneratingCard job={job} onCancel={(id) => void cancelJob(id)} />
                {job.status === 'failed' && (
                  <button
                    onClick={() => removeJob(job.jobId)}
                    className="absolute top-3 right-3 text-gray-300 hover:text-gray-500 text-lg leading-none"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {orphanedSkeletons.map((p) => (
              <OrphanedGeneratingCard key={p.id} project={p} />
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {projects
          .filter(
            (p) =>
              p.isDemo || (
                (p.data != null || (p.subfunctionCount != null && p.subfunctionCount > 0) || (p.automationBuildCount != null && p.automationBuildCount > 0) || (p.totalFteCount != null)) &&
                (!p.data?.subfunctions || p.data.subfunctions.every((sf) => Object.keys(sf.roles ?? {}).length > 0))
              ),
          )
          .map((project) => (
          <div
            key={project.id}
            onClick={() => navigate(`/dashboard/${project.id}`)}
            className="bg-white rounded-2xl p-6 border border-gray-100 cursor-pointer card-hover relative group"
          >
            {/* Brand color stripe */}
            <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{ background: project.branding?.primaryColor || '#A100FF' }}
            />

            {/* Delete button */}
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(project.id) }}
              aria-label={`Delete ${project.name || project.client}`}
              className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-red-400 text-lg leading-none"
            >
              ×
            </button>

            <div className="pt-2">
              <div className="flex items-start gap-3 mb-3">
                {/* Color avatar */}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ background: project.branding?.primaryColor || '#A100FF' }}
                >
                  {(project.client || project.name || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  {renamingProjectId === project.id ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <input
                          ref={cardRenameInputRef}
                          type="text"
                          value={renameValue}
                          maxLength={100}
                          disabled={renameSaving}
                          aria-label="Rename dashboard"
                          className="text-sm font-semibold text-gray-900 border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:border-purple-400 w-full disabled:opacity-50"
                          onChange={(e) => { setRenameValue(e.target.value); setRenameError('') }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !renameSaving) void confirmCardRename(project)
                            if (e.key === 'Escape' && !renameSaving) cancelCardRename(e)
                          }}
                        />
                        <button
                          onClick={() => void confirmCardRename(project)}
                          disabled={renameSaving}
                          className="text-xs text-white bg-purple-600 hover:bg-purple-700 px-2 py-0.5 rounded font-semibold flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >{renameSaving ? '…' : '✓'}</button>
                        <button
                          onClick={(e) => cancelCardRename(e)}
                          disabled={renameSaving}
                          className="text-xs text-gray-500 hover:text-gray-700 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >✕</button>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        {renameError && <span className="text-[10px] text-red-500">{renameError}</span>}
                        <span className="text-[10px] text-gray-400 ml-auto">{renameValue.length}/100</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 group min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{project.name || project.client}</h3>
                      {/* Only the owner may rename — the list includes shared/admin-visible
                          projects, so gate on ownership exactly like the Dashboard pencil. */}
                      {(api.platform === 'desktop' || (!!currentUserEmail && project.createdBy === currentUserEmail)) && (
                        <button
                          onClick={(e) => startCardRename(e, project)}
                          disabled={renameSaving}
                          // Named per card, like the × button, so each card's pencil is distinguishable.
                          aria-label={`Rename ${project.name || project.client}`}
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-gray-700 flex-shrink-0 disabled:opacity-0"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                            <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.75 9.775a.75.75 0 0 0-.197.384l-.5 2.5a.75.75 0 0 0 .888.888l2.5-.5a.75.75 0 0 0 .384-.197l7.263-7.263a1.75 1.75 0 0 0 0-2.475ZM11.78 3.575a.25.25 0 0 1 .354 0l.29.29a.25.25 0 0 1 0 .354L11.5 5.147l-.644-.644 .924-.928ZM4.933 10.081l5.52-5.52.644.644-5.52 5.52-.784.157.157-.784-.017.003Z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 truncate">{project.industry}</p>
                </div>
              </div>

              <div className="mt-4 grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <ModeBadge mode={project.mode} />
                  {currentUserEmail && project.sharedWith?.includes(currentUserEmail) && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-500">
                      Shared
                    </span>
                  )}
                </div>
                <span
                  className="min-w-0 truncate whitespace-nowrap text-[11px] text-gray-400"
                  title={timestampsByProjectId.get(project.id)?.original}
                >
                  {timestampsByProjectId.get(project.id)?.formatted}
                </span>
                <div className="flex flex-shrink-0 items-center">
                  <ScopeBadge functionScope={project.profile?.functionScope} />
                </div>
              </div>

              {(project.data || (project.subfunctionCount != null && project.subfunctionCount > 0) || (project.automationBuildCount != null && project.automationBuildCount > 0) || (project.totalFteCount != null)) && (
                <div className="mt-3 grid grid-cols-[repeat(3,minmax(0,1fr))] gap-2">
                  <div className="min-w-0 bg-gray-50 rounded-lg px-2 py-2 text-center">
                    <div className="text-base sm:text-lg font-semibold tabular-nums" style={{ color: project.branding?.primaryColor || '#A100FF' }}>
                      {project.data?.subfunctions?.length ?? project.subfunctionCount ?? 0}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wide leading-tight">Functions</div>
                  </div>
                  <div className="min-w-0 bg-gray-50 rounded-lg px-2 py-2 text-center">
                    <div className="text-base sm:text-lg font-semibold tabular-nums" style={{ color: project.branding?.primaryColor || '#A100FF' }}>
                      {project.data?.automation_builds?.length ?? project.automationBuildCount ?? 0}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wide leading-tight">AI Builds</div>
                  </div>
                  <div className="min-w-0 bg-gray-50 rounded-lg px-2 py-2 text-center">
                    <div className="text-base sm:text-lg font-semibold tabular-nums" style={{ color: project.branding?.primaryColor || '#A100FF' }}>
                      {formatFte(project)}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wide leading-tight">FTE</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Inline delete confirmation — avoids window.confirm() which breaks Electron input focus */}
    {uploadAllResult && (
      <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-semibold text-white transition-all ${uploadAllResult.failed === 0 ? 'bg-green-500' : 'bg-red-500'}`}>
        {uploadAllResult.failed === 0
          ? `✓ ${uploadAllResult.success} navigator${uploadAllResult.success !== 1 ? 's' : ''} published to web`
          : `${uploadAllResult.success} published, ${uploadAllResult.failed} failed`}
      </div>
    )}

    {confirmDeleteId && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        onClick={() => setConfirmDeleteId(null)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="home-delete-title"
          className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="home-delete-title" className="text-base font-semibold text-gray-900 mb-2">Delete this project?</h3>
          <p className="text-sm text-gray-500 mb-5">This action cannot be undone.</p>
          <div className="flex gap-3">
            <button
              onClick={() => setConfirmDeleteId(null)}
              className="flex-1 py-2 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="flex-1 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
